// 채널톡 타입 정의
// Window 인터페이스는 react-app-env.d.ts에서 정의됨

// 채널톡 설정 인터페이스
export interface ChannelIOSettings {
  pluginKey: string;
  accessSecret: string;
  memberId?: string;
  profile?: ChannelIOUserInfo;
  hideDefaultLauncher?: boolean;
  hideChannelButtonOnBoot?: boolean;
  customLauncherSelector?: string;
  mobileMessengerMode?: "full" | "slideup";
  language?: string;
  unsubscribeEmail?: boolean;
  unsubscribeTexting?: boolean;
  trackDefaultEvent?: boolean;
  trackUtmSource?: boolean;
  trackUtmMedium?: boolean;
  trackUtmCampaign?: boolean;
  trackUtmTerm?: boolean;
  trackUtmContent?: boolean;
  trackClickEvent?: boolean;
  zIndex?: number;
}

// 사용자 정보 인터페이스
export interface ChannelIOUserInfo {
  name?: string;
  mobileNumber?: string;
  avatarUrl?: string;
  email?: string;
  [key: string]: any;
}

// 채널톡 플러그인 키와 액세스 시크릿
const CHANNEL_IO_PLUGIN_KEY = "4de468b6-e6bf-4446-a6d0-fb3ccb1132a6";
const CHANNEL_IO_ACCESS_SECRET = "20157795b28f1efefb6415a2686cca15";

/**
 * 현재 경로가 홈인지 확인하는 함수
 * URL의 해시 경로나 pathname을 기준으로 판단
 */
export const isHomePage = (): boolean => {
  // GitHub Pages 환경 감지
  const isGitHubPages = window.location.hostname.includes("github.io");

  // 기본 경로 확인
  const pathname = window.location.pathname;

  // 해시 라우팅 (#/ 형태) 확인
  const hash = window.location.hash.replace("#", "");

  // 개발 환경에서 디버깅을 위한 로그


  // GitHub Pages 환경에서는 /home/ 경로가 기본 경로임
  if (isGitHubPages) {
    // /home/ 또는 /home 경로이면서 해시가 빈 값이거나 '/'인 경우
    if (pathname === "/home/" || pathname === "/home") {
      return hash === "" || hash === "/" || hash === "/home";
    }
  } else {
    // 로컬 개발 환경에서의 홈 경로 확인 - 여기를 수정
    // 로컬에서는 주로 pathname이 '/'이고, 해시가 비어있거나 '/'인 경우가 홈
    if (pathname === "/" || pathname === "/home") {
      return hash === "" || hash === "/" || hash === "/home";
    }

    // 로컬 환경에서는 pathname이 빈 문자열일 수도 있음
    if (pathname === "") {
      return hash === "" || hash === "/" || hash === "/home";
    }
  }

  return false;
};

/**
 * 채널톡 초기화
 * @param settings 채널톡 설정
 * @param forceInit 강제 초기화 여부 (기본값: false)
 */
export const bootChannelTalk = (
  settings?: Partial<ChannelIOSettings>,
  forceInit: boolean = false
) => {
  if (process.env.NODE_ENV === "development") {
  }

  if (!window.ChannelIO) {
    console.error("ChannelIO is not loaded yet");
    return;
  }

  // 기본 설정과 사용자 설정 병합
  const defaultSettings: ChannelIOSettings = {
    pluginKey: CHANNEL_IO_PLUGIN_KEY,
    accessSecret: CHANNEL_IO_ACCESS_SECRET, // 액세스 시크릿 추가
    language: "en", // 영어로 변경 (이전: 'ko')
    mobileMessengerMode: "slideup",
    // 하단 중앙에 위치하도록 커스텀 CSS 설정
    customLauncherSelector: "#custom-channel-button",
    hideDefaultLauncher: true,
    // 채널톡 버튼의 z-index를 높게 설정하여 다른 요소보다 앞에 보이도록 함
    zIndex: 10000,
  };

  const bootSettings = {
    ...defaultSettings,
    ...settings,
  };

  try {
    window.ChannelIO("boot", bootSettings);

    // 커스텀 채널톡 버튼 생성 및 스타일링
    setTimeout(() => {
      createCustomChannelButton();
    }, 1000); // 채널톡 로드 후 약간의 지연 시간을 두고 커스텀 버튼 생성
  } catch (error) {
    console.error("Failed to boot ChannelTalk:", error);
  }
};

/**
 * 채널톡 종료
 * 종료 후 관련 DOM 요소도 함께 제거
 */
export const shutdownChannelTalk = () => {
  if (window.ChannelIO) {
    try {
      // 채널톡 종료
      window.ChannelIO("shutdown");

      // DOM에서 채널톡 관련 요소 제거
      hideAllChannelTalkElements();
    } catch (error) {
      console.error("Failed to shutdown ChannelTalk:", error);
    }
  }
};

/**
 * 채널톡 스크립트가 완전히 로드되었는지 확인
 */
const isChannelIOReady = (): boolean => {
  // window.ChannelIOScriptLoaded 플래그가 있으면 스크립트 로드 완료
  if ((window as any).ChannelIOScriptLoaded) {
    return true;
  }

  // 기존 방식: queue가 없으면 로드 완료로 판단
  return !!(
    window.ChannelIO &&
    typeof window.ChannelIO === "function" &&
    !window.ChannelIO.q
  );
};

/**
 * 채널톡 스크립트 로드 완료를 기다리는 함수
 */
const waitForChannelIOReady = (timeout: number = 5000): Promise<boolean> => {
  return new Promise((resolve) => {
    const startTime = Date.now();

    const checkReady = () => {
      if (isChannelIOReady()) {
        resolve(true);
        return;
      }

      if (Date.now() - startTime > timeout) {
        console.error("ChannelIO 스크립트 로드 타임아웃");
        resolve(false);
        return;
      }

      setTimeout(checkReady, 100);
    };

    checkReady();
  });
};

/**
 * 채널톡 메신저 표시
 * 채널톡이 초기화되지 않은 경우 자동으로 초기화 후 메신저를 표시
 */
export const showChannelTalk = async () => {
  if (!window.ChannelIO) {
    console.error("ChannelIO is not loaded yet");
    return;
  }

  // 채널톡 스크립트가 완전히 로드될 때까지 기다림
  const isReady = await waitForChannelIOReady();
  if (!isReady) {
    console.error("ChannelIO 스크립트 로드 실패");
    return;
  }

  try {
    window.ChannelIO("showMessenger");
  } catch (error) {
    try {
      // 채널톡이 초기화되지 않았다면 강제 초기화
      bootChannelTalk({}, true);

      // 초기화 후 약간의 지연을 두고 메신저 표시
      setTimeout(() => {
        try {
          if (window.ChannelIO) {
            window.ChannelIO("showMessenger");
          }
        } catch (showError) {
          console.error(
            "Failed to show messenger after initialization:",
            showError
          );
        }
      }, 1000);
    } catch (bootError) {
      console.error("Failed to boot ChannelTalk:", bootError);
    }
  }
};

/**
 * 채널톡 메신저 숨기기
 */
export const hideChannelTalk = () => {
  if (window.ChannelIO) {
    try {
      window.ChannelIO("hideMessenger");
    } catch (error) {
      console.error("Failed to hide ChannelTalk messenger:", error);
    }
  }
};

/**
 * DOM에서 모든 채널톡 관련 요소를 찾아 숨기는 함수
 * 채널톡 API를 통한 shutdown이 즉시 반영되지 않는 경우 대비
 */
export const hideAllChannelTalkElements = () => {
  // 유사한 클래스나 ID를 가진 모든 채널톡 관련 요소 찾기
  const findAndHideElements = (selector: string) => {
    const elements = document.querySelectorAll(selector);
    elements.forEach((el) => {
      const element = el as HTMLElement;
      if (element) {
        element.style.display = "none";
        element.style.visibility = "hidden";
        element.style.opacity = "0";
        element.style.pointerEvents = "none";
      }
    });
  };

  // 1. 채널톡의 커스텀 버튼
  const customButton = document.getElementById("custom-channel-button");
  if (customButton) {
    customButton.style.display = "none";
  }

  // 2. 'channel-' 접두사를 가진 클래스 요소
  findAndHideElements('[class*="channel-"]');

  // 3. 채널톡 iframe 요소
  const iframes = document.querySelectorAll("iframe");
  iframes.forEach((iframe) => {
    if (iframe.src.includes("channel.io")) {
      iframe.style.display = "none";
    }
  });

  // 4. position: fixed를 가진 요소들 중 채널톡으로 의심되는 요소
  const fixedElements = document.querySelectorAll(
    'div[style*="position: fixed"], button[style*="position: fixed"]'
  );
  fixedElements.forEach((el) => {
    const element = el as HTMLElement;
    // 채널톡 버튼으로 의심되는 요소만 처리
    if (
      element.style.zIndex &&
      parseInt(element.style.zIndex) > 100 &&
      (element.style.borderRadius === "50%" ||
        element.classList.contains("launcher"))
    ) {
      element.style.display = "none";
    }
  });

};

/**
 * 특정 이벤트 추적
 * @param event 이벤트 이름
 * @param params 이벤트 파라미터
 */
export const trackChannelTalkEvent = (event: string, params?: any) => {
  if (window.ChannelIO) {
    try {
      window.ChannelIO("track", event, params);
    } catch (error) {
      console.error("Failed to track ChannelTalk event:", error);
    }
  }
};

/**
 * 사용자 정보 업데이트
 * @param userInfo 사용자 정보
 */
export const updateChannelTalkUser = (userInfo: ChannelIOUserInfo) => {
  if (window.ChannelIO) {
    try {
      window.ChannelIO("updateUser", userInfo);
    } catch (error) {
      console.error("Failed to update ChannelTalk user:", error);
    }
  }
};

/**
 * 현재 페이지 설정
 * @param pageName 페이지 이름
 */
export const setChannelTalkPage = (pageName: string) => {
  if (window.ChannelIO) {
    try {
      // 직접 호출 방식 사용
      window.ChannelIO("setPage", pageName);
    } catch (error) {
      console.error("Failed to set ChannelTalk page:", error);
    }
  }
};

/**
 * 커스텀 채널톡 버튼 생성
 * 화면 하단에 고정된 채팅 버튼을 생성합니다.
 * 현재는 Navigation Bar의 Support 버튼을 사용하므로 비활성화됨
 */
const createCustomChannelButton = () => {

  // 기존에 생성된 버튼이 있다면 제거
  const existingButton = document.getElementById("custom-channel-button");
  if (existingButton) {
    existingButton.remove();
  }

  return;
};

/**
 * 채널톡 언어 설정 변경
 * @param language 변경할 언어 코드
 */
export const updateChannelTalkLanguage = (language: string) => {
  if (window.ChannelIO) {
    try {
      // 채널톡 재초기화
      window.ChannelIO("shutdown");

      // 약간의 지연 후 다시 시작
      setTimeout(() => {
        bootChannelTalk(
          {
            language: language,
          },
          true
        );
      }, 300);
    } catch (error) {
      console.error("Failed to update ChannelTalk language:", error);
    }
  }
};
